import React from 'react';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import GroupsIcon from '@mui/icons-material/Groups';
import TuneIcon from '@mui/icons-material/Tune';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DestinationShowcase from './DestinationShowcase';

export { states } from './stateData';

const categories = [
  { key: 'all', label: 'All Tours' },
  { key: 'adventure', label: 'Adventure' },
  { key: 'cultural', label: 'Cultural' },
  { key: 'relaxation', label: 'Relaxation' },
  { key: 'wildlife', label: 'Wildlife' },
  { key: 'off-beat', label: 'Off-beat' },
  { key: 'handpicked', label: 'Handpicked' },
];

const MODES = {
  group: {
    label: 'Group Escapes',
    sub: 'Pre-planned dates, guide & co-travellers',
    note: 'Perfect for social travellers seeking premium curated group experiences!',
    Icon: GroupsIcon,
  },
  private: {
    label: 'Private Escapes',
    sub: '100% tailor-made, private transfers',
    note: 'Fully customised private trips tailor-made to your calendar!',
    Icon: TuneIcon,
  },
};

function FilterSectionImpl({
  selectedCategory,
  onCategoryChange,
  selectedSort,
  onSortChange,
  selectedState,
  onStateChange,
  tripType,            // 'group' | 'private'
  onTripTypeChange,    // (type) => void
  searchTerm,          // used to highlight an international destination
  onDestinationPick,   // ({type:'state'|'country', key, label}) => void
  onDestinationClear,  // () => void
}) {
  const mode = tripType === 'private' ? 'private' : 'group';

  return (
    <>
    {/* Sand band: switch + destinations */}
    <div className="bg-[#FAF7F2]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-10">
      {/* Group / Private switch */}
      <div className="flex flex-col items-center mb-12">
        <div className="relative w-full max-w-2xl rounded-2xl bg-white p-1.5 ring-1 ring-[#E8E0D2] shadow-[0_10px_25px_-8px_rgba(15,41,66,0.18)]">
          <div
            className={`absolute top-1.5 bottom-1.5 left-1.5 w-[calc(50%-6px)] rounded-xl bg-white ring-1 ring-blue-300 shadow-md transition-transform duration-500 ease-out ${
              mode === 'private' ? 'translate-x-[calc(100%+12px)]' : 'translate-x-0'
            }`}
          />
          <div className="relative z-10 grid grid-cols-2 gap-2">
            {Object.entries(MODES).map(([key, m]) => {
              const active = mode === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => onTripTypeChange && onTripTypeChange(key)}
                  className="flex flex-col sm:flex-row items-center justify-center gap-3 rounded-xl py-3 px-4 text-center sm:text-left"
                >
                  <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg transition-colors ${
                    active ? (key === 'group' ? 'bg-blue-100 text-blue-700' : 'bg-sky-100 text-sky-700') : 'bg-blue-100 text-blue-400'
                  }`}>
                    <m.Icon fontSize="small" />
                  </div>
                  <div>
                    <span className={`block text-sm ${active ? 'font-bold text-slate-900' : 'font-semibold text-slate-500'}`}>{m.label}</span>
                    <p className="hidden sm:block text-[11px] text-slate-500 font-medium">{m.sub}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        <div className={`mt-4 inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-semibold border transition-all duration-500 ${
          mode === 'group' ? 'bg-white text-blue-700 border-blue-200' : 'bg-white text-sky-700 border-sky-200'
        }`}>
          <AutoAwesomeIcon sx={{ fontSize: 14 }} />
          <span>{MODES[mode].note}</span>
        </div>
      </div>

      {/* Dynamic destinations (India / International) */}
      <DestinationShowcase
        selectedState={selectedState}
        searchTerm={searchTerm}
        onPick={onDestinationPick}
        onClear={onDestinationClear}
      />

    </div>
    </div>

    {/* Light-blue band: Featured Tours heading + pills (Tours grid continues this band) */}
    <div className="bg-[#EEF4FF]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-4">
      {/* Section heading */}
      <div className="flex flex-col gap-1 mb-5">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-blue-600">Hand-picked collections</p>
        <h2 className="text-3xl sm:text-4xl font-bold text-[#0B2A5B]">Featured Tours</h2>
        <p className="text-slate-600 max-w-2xl">Extraordinary journeys designed to create unforgettable memories.</p>
      </div>

      {/* Category pills + Sort */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between border-t border-blue-200 pt-5">
        <div className="flex items-center gap-2 overflow-x-auto py-1" style={{ scrollbarWidth: 'none' }}>
          {categories.map((cat) => {
            const active = selectedCategory === cat.key;
            return (
              <button
                key={cat.key}
                onClick={() => onCategoryChange(cat.key)}
                type="button"
                className={`rounded-full px-5 py-2 text-xs md:text-sm font-bold transition-all whitespace-nowrap ${
                  active ? 'bg-blue-600 text-white shadow-md shadow-blue-600/25' : 'bg-white border border-blue-200 text-slate-700 hover:border-blue-500 hover:text-blue-700'
                }`}
              >
                {cat.label}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider whitespace-nowrap">Sort by</span>
          <FormControl
            variant="outlined"
            size="small"
            sx={{
              minWidth: 170,
              '& .MuiOutlinedInput-root': { borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600, color: '#334155', '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2563eb' } },
            }}
          >
            <Select value={selectedSort} onChange={(e) => onSortChange(e.target.value)} displayEmpty>
              <MenuItem value="">Default</MenuItem>
              <MenuItem value="popular">Most Popular</MenuItem>
              <MenuItem value="price-low">Price: Low to High</MenuItem>
              <MenuItem value="price-high">Price: High to Low</MenuItem>
              <MenuItem value="rating">Highest Rated</MenuItem>
            </Select>
          </FormControl>
        </div>
      </div>
    </div>
    </div>
    </>
  );
}

export const FilterSection = React.memo(FilterSectionImpl);